/**
 * How the journal writes a number down.
 *
 * PURE, and that is the point of the folder: no React, no clock of its own,
 * no fetch. Every one of these is a total function of its argument, so the
 * tests need no DOM.
 *
 * ONE CONVERSION, IN ONE PLACE. `ivPct` exists because implied volatility is
 * stored as a FRACTION and shown as a percentage, and a second multiplication
 * somewhere else is how a 14.2% vol becomes 1420%.
 */

import { compactInr } from '@/screens/terminal/quantity'

/** Calendar months need an entry date; an average duration has no calendar anchor. */
export function holdText(minutes: number | null, openedAt?: string): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes < 0) return '—'
  let remaining = Math.floor(minutes)
  let months = 0
  if (openedAt && Number.isFinite(Date.parse(openedAt))) {
    // Shift IST's fixed offset into UTC fields so the host timezone cannot
    // change month boundaries. Clamp entries on the 29th–31st to month end.
    const start = new Date(Date.parse(openedAt) + 330 * 60000)
    const end = new Date(start.getTime() + remaining * 60000)
    months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + end.getUTCMonth() - start.getUTCMonth()
    const afterMonths = (count: number) => {
      const cursor = new Date(start)
      cursor.setUTCDate(1)
      cursor.setUTCMonth(start.getUTCMonth() + count)
      const lastDay = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0)).getUTCDate()
      cursor.setUTCDate(Math.min(start.getUTCDate(), lastDay))
      return cursor.getTime()
    }
    while (months > 0 && afterMonths(months) > end.getTime()) months--
    remaining = Math.floor((end.getTime() - afterMonths(months)) / 60000)
  }
  const days = Math.floor(remaining / 1440)
  const units = [
    [Math.floor(months / 12), 'Y'], [months % 12, 'M'],
    [Math.floor(days / 7), 'W'], [days % 7, 'd'],
    [Math.floor(remaining % 1440 / 60), 'h'], [remaining % 60, 'm'],
  ] as const
  const first = units.findIndex(([value]) => value > 0)
  return units.slice(first < 0 ? -1 : first).map(([value, unit]) => `${value}${unit}`).join(' ')
}

/** Full entry date, matching the journal's IST timestamps. */
export const entryDate = (iso: string | null): string =>
  iso === null || !Number.isFinite(Date.parse(iso)) ? '—' : new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
  })

/** IV is stored as a FRACTION. One place converts it; nothing else does. */
export const ivPct = (iv: number | null): string => (iv === null ? '—' : (iv * 100).toFixed(1))

/** HH:MM in IST. */
export const hhmm = (iso: string | null): string =>
  iso === null
    ? '—'
    : new Date(iso).toLocaleTimeString('en-GB', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })

/** Execution-level timestamps need seconds; ordinary list rows remain compact. */
export const hhmmss = (iso: string): string => new Date(iso).toLocaleTimeString('en-GB', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
})

/** `19 OCT`. */
export const dayLabel = (iso: string | null): string =>
  iso === null
    ? '—'
    : new Date(iso)
        .toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' })
        .toUpperCase()

/** A signed rupee figure, compacted. `+₹7.8k`, `-₹2.1k`. */
export const money = (v: number): string =>
  `${v >= 0 ? '+' : '-'}₹${compactInr(Math.abs(v))}`

/**
 * "3/7", or an em dash when no checklist was recorded.
 *
 * Null and empty are DIFFERENT and read differently: null is "we did not
 * write it down", empty is "this strategy asks nothing". Collapsing them
 * would show 0/0 on a trade whose rules were simply never captured.
 */
export const rulesFollowed = (
  checklist: readonly { rule: string; ticked: boolean }[] | null | undefined,
): string => {
  // `undefined` as well as null: a response from a server that predates the
  // field must render, not throw.
  if (!checklist || checklist.length === 0) return '—'
  return `${checklist.filter((c) => c.ticked).length}/${checklist.length}`
}

/**
 * The one contract a row is about, or null when it is about several.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "NIFTY BASKET" NAMES NOTHING YOU CAN ACT ON
 *
 * A loose chain click has no strategy, so the STRATEGY column fell through to
 * `${underlying} basket` — and a page of them reads as a dozen identical
 * rows. Which strike? Which side of the chain? Which expiry? All of it was
 * already on the row, one click away, and none of it was on the row itself.
 * Reported 2026-11-03.
 *
 * `leg.symbol` is built by the same `contractLabel` the drawer uses, so the
 * journal and the positions book name a contract identically rather than
 * formatting it twice.
 *
 * NULL WHEN THE ROW SPANS CONTRACTS, which is every real basket — an Iron
 * Condor is four strikes and naming it after one of them would be a lie that
 * reads like a fact. A folded group (9.23) is one contract by construction,
 * so it gets its name here too.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function rowContract(trade: {
  strategyName: string | null
  legs: readonly { symbol: string; strike: number; optionType: string; expiry: string }[]
}): string | null {
  // A named strategy already has a better name than a contract.
  if (trade.strategyName) return null
  const [first] = trade.legs
  if (!first) return null
  for (const l of trade.legs) {
    if (l.strike !== first.strike || l.optionType !== first.optionType || l.expiry !== first.expiry) {
      return null
    }
  }
  return first.symbol
}

/**
 * What a journal row calls a trade — ROADMAP 9.39.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR PLACES ASKED, AND ONLY ONE OF THEM KNEW ABOUT `label`
 *
 * The row, its aria-label, the review panel's subject and the contract
 * fallback each derived this themselves, and three of the four asked only
 * for `strategyName`. So a chain combo or a hand-grouped basket — which
 * carry a `label` and no strategy — read "NIFTY basket" in the column and
 * something else again in the screen reader.
 *
 * The precedence, now in one place:
 *
 *   strategyName   the template that fired it, PREFIXED with the underlying
 *                  because a strategy is fired on many and "Iron Condor"
 *                  alone does not say which
 *   label          what the trader called THIS position. Already about one
 *                  thing, so it is printed as written
 *   the contract   a loose click is named by its strike (9.24)
 *   the underlying the last resort, and the one that said nothing
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function journalTradeName(trade: {
  underlying: string
  strategyName: string | null
  label: string | null
  legs: readonly { symbol: string; strike: number; optionType: string; expiry: string }[]
}): string {
  if (trade.strategyName) return `${trade.underlying} ${trade.strategyName}`
  return trade.label ?? rowContract(trade) ?? `${trade.underlying} basket`
}
